import React, { useState, useRef, Dispatch, useEffect, useMemo } from 'react';
import { Box, CircularProgress, Stack, IconButton, Tooltip } from '@mui/material';
import {
  Update as UpdateIcon,
  CachedOutlined as CachedIcon,
  FileCopy as FileCopyIcon,
  Done as DoneIcon
} from '@mui/icons-material';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { ApplicationAction, IApplication } from '../types';
import AISummaryNoAccess from './AISummaryNoAccess';
import { styles } from '../styles';
import { FelixConfused } from '../../../AIStudio/SharedComponents/Graphics/FelixSVG';
import DisplayHTMLViewOnly from '../../../AIStudio/SharedComponents/DisplayHTMLViewOnly';
import { handleRichCopy } from '../../Components/Utilities/handleRichCopy';
import { LoadingOverlay } from '../../../AIStudio/AIStudioModal/components/LoadingOverlay';
import LoadingButton from '../../Components/Buttons/LoadingButton';
import { DynamicFooterWithInput } from '../../../AIStudio/SharedComponents/Footers/DynamicFoorterWithInput';
import { useWebSockets } from '../../Components/Utilities/useWebSockets';
import { IWebsocketData } from '../../../AIStudio/SharedComponents/types';
import { convertToLocalEnglish } from '../../Components/Utilities/convertToLocalEnglish';
import { classes as aiStudioClasses } from '../../../AIStudio/AIStudioModal/styles';
import dayjs from 'dayjs';
import Api from '../API';

const SUGGESTED_PROMPTS = [
  'Make it more objective',
  'Balance strengths and weaknesses',
  'Focus more on resume',
  'Weigh screening questions more',
  'Highlight job-relevant experience'
];

export default function AISummary({
  dispatch,
  aiSummariesEnabled
}: {
  dispatch: Dispatch<ApplicationAction>;
  aiSummariesEnabled: boolean;
}) {
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInputOpen, setEmailInputOpen] = useState<boolean>(false);
  const [copied, setCopied] = useState(false);
  const [isApplicationSaved, setIsApplicationSaved] = useState(false);
  const [summaryData, setSummaryData] = useState<IWebsocketData | null>(null);
  const [generatingApplicationId, setGeneratingApplicationId] = useState<number | null>(null);
  const queryClient = useQueryClient();
  const application = queryClient.getQueryData<IApplication>(['application']);
  const summaryRef = useRef<HTMLDivElement | null>(null);

  const downloadPDF = () => {
    const url = `/api/v4/jobs/${application?.job.id}/applications/${application?.id}/application_summary.pdf`;
    window.open(url, '_blank');
  };

  const { subscribeToChannel, unsubscribeFromChannel, socketConnected } = useWebSockets(
    'AiStudioChannel',
    (data: IWebsocketData, UID?: number) => {
      if (data.UID !== UID) return;
      if (data.kind === 'error') {
        dispatch({
          type: 'SET_SNACKBAR',
          payload: {
            message:
              'Summary generation failed. Please try again or contact support if the issue persists.',
            state: 'error'
          }
        });
        setGeneratingApplicationId(null);
      } else {
        setSummaryData(data);
        setGeneratingApplicationId(null);
        setIsApplicationSaved(false);

        // Update only the summary in the application cache
        queryClient.setQueryData(['application'], (oldData: IApplication | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            summary: data.content,
            summary_updated_at: new Date().toISOString()
          };
        });
      }
      unsubscribeFromChannel();
    }
  );

  const { mutate: sendEmail, isLoading: sendingEmails } = useMutation({
    mutationFn: async () => {
      if (application) {
        await Api.sendApplicationSummary(application.job.id, application.id, emails);
      }
    },
    onSuccess: () => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: `Email has been sent successfully`, state: 'success' }
      });
      setEmailInputOpen(false);
      setEmails([]);
    },
    onError: (error: { error: string }) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message: `There was an error in sending email, ${error.error}`,
          state: 'error'
        }
      });
    }
  });

  const { mutate: generateSummary, isLoading: updatingSummary } = useMutation({
    mutationFn: async (feedbackInput?: string) => {
      if (application) {
        const UID = Date.now();
        const targetApplicationId = application.id;

        const request = await fetch(
          `/api/v4/jobs/${application.job.id}/applications/${application.id}/application_summary`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              UID: UID,
              ...(feedbackInput && { feedback: feedbackInput })
            })
          }
        );

        if (request.ok) {
          dispatch({
            type: 'SET_SNACKBAR',
            payload: {
              message: 'Summary is being generated, this may take a few minutes',
              state: 'success'
            }
          });
          setGeneratingApplicationId(targetApplicationId);
          subscribeToChannel(UID);
        } else {
          const response = await request.json();
          throw new Error(response.errors || 'Error requesting a generated summary');
        }
      }
    },
    onError: (error) => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message:
            error instanceof Error
              ? error.message
              : 'Failed to start summary generation. Please try again.',
          state: 'error'
        }
      });
      setGeneratingApplicationId(null);
    }
  });

  const isGenerating = useMemo(
    () => updatingSummary || (generatingApplicationId === application?.id && !socketConnected),
    [updatingSummary, generatingApplicationId, application?.id, socketConnected]
  );

  const isModalLoading = useMemo(
    () => socketConnected && generatingApplicationId === application?.id,
    [socketConnected, generatingApplicationId, application?.id]
  );

  const canInteract = !isGenerating;

  useEffect(() => {
    return () => {
      unsubscribeFromChannel();
    };
  }, [unsubscribeFromChannel]);

  const handleSaveToApplication = async () => {
    try {
      if (application) {
        const request = await fetch(
          `/api/v4/jobs/${application.job.id}/application_summaries/update_summary`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              application_summary: {
                history_id: summaryData?.id || application.id
              }
            })
          }
        );
        if (request.ok) {
          setIsApplicationSaved(true);
          dispatch({
            type: 'SET_SNACKBAR',
            payload: {
              message: 'Summary saved successfully to application page',
              state: 'success'
            }
          });
        } else {
          const response = await request.json();
          throw new Error(response.errors || 'Failed to save summary. Please try again.');
        }
      }
    } catch (error) {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: {
          message:
            error instanceof Error ? error.message : 'Failed to save summary. Please try again.',
          state: 'error'
        }
      });
    }
  };

  const handleRefineSubmit = (input?: string) => {
    generateSummary(input);
  };

  return (
    <Stack sx={styles.AISummaryContainer}>
      <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack sx={{ fontSize: '16px', fontWeight: 600 }}>Felix AI Summary</Stack>
        <Stack sx={{ flexDirection: 'row', columnGap: 1, alignItems: 'center' }}>
          <Tooltip placement="top" title="Regenerate summary" arrow>
            <IconButton
              id="regenerate-summary"
              disabled={!canInteract}
              onClick={() => generateSummary(undefined)}
              sx={{
                ...aiStudioClasses.actionButton,
                ...(isGenerating && {
                  animation: 'pulse 2s infinite',
                  '@keyframes pulse': {
                    '0%': { opacity: 1 },
                    '50%': { opacity: 0.5 },
                    '100%': { opacity: 1 }
                  }
                })
              }}
              size="small"
              aria-label="Regenerate AI summary"
              aria-describedby="regenerate-summary-description"
            >
              <CachedIcon />
            </IconButton>
          </Tooltip>
          <Box id="regenerate-summary-description" className="sr-only">
            Regenerate the AI summary for this application
          </Box>

          <Tooltip title={`${copied ? 'Copied!' : 'Copy summary'}`} placement="top">
            <IconButton
              id="copy-tlp-html"
              sx={aiStudioClasses.actionButton}
              size="small"
              disabled={!canInteract || !summaryData}
              onClick={() => handleRichCopy(summaryRef.current, setCopied)}
              aria-label={`${copied ? 'Summary copied' : 'Copy summary to clipboard'}`}
              aria-describedby="copy-summary-description"
            >
              {copied ? <DoneIcon /> : <FileCopyIcon />}
            </IconButton>
          </Tooltip>
          <Box id="copy-summary-description" className="sr-only">
            Copy the AI summary to your clipboard
          </Box>

          <LoadingButton
            id="save-application-summary"
            sx={{
              ...aiStudioClasses.footerContinueButton,
              minWidth: '255px',
              ...(isApplicationSaved && {
                backgroundColor: '#4caf50',
                color: 'white',
                '&:hover': {
                  backgroundColor: '#45a049'
                }
              })
            }}
            task={handleSaveToApplication}
            disabled={isApplicationSaved || isGenerating || !summaryData}
            loadingCaption="Saving..."
            aria-label={
              isApplicationSaved
                ? 'Summary saved to application'
                : 'Save summary to application page'
            }
            aria-describedby="save-summary-description"
          >
            {isApplicationSaved ? (
              <>
                <DoneIcon sx={{ mr: 1, fontSize: '18px' }} />
                Saved to application
              </>
            ) : (
              "Save to application's page"
            )}
          </LoadingButton>
          <Box id="save-summary-description" className="sr-only">
            Save the current AI summary to the application page
          </Box>
        </Stack>
      </Stack>
      {aiSummariesEnabled ? (
        <>
          {application?.summary ? (
            <Stack sx={{ rowGap: 2, position: 'relative' }}>
              <Stack
                sx={{
                  flexDirection: 'row',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  gap: 2,
                  fontSize: '12px',
                  color: '#666',
                  fontWeight: 400,
                  mt: 0.5
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <UpdateIcon sx={{ transform: 'scaleX(-1)', fontSize: '12px' }} />
                  Last updated {dayjs(application?.summary_updated_at).format('D/M/YY, LT')}
                </Box>
                {isApplicationSaved && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: '#4caf50',
                      fontWeight: 500
                    }}
                    role="status"
                    aria-live="polite"
                  >
                    <DoneIcon sx={{ fontSize: '12px' }} />
                    Saved
                  </Box>
                )}
              </Stack>
              <Box sx={{ position: 'relative' }}>
                <DisplayHTMLViewOnly
                  content={
                    summaryData ? convertToLocalEnglish(summaryData.content) : application.summary
                  }
                  viewerRef={summaryRef}
                  onExportClick={downloadPDF}
                  setEmails={setEmails}
                  emails={emails}
                  onSendEmailClick={sendEmail}
                  emailInputOpen={emailInputOpen}
                  setEmailInputOpen={setEmailInputOpen}
                  sendingEmails={sendingEmails}
                  sendEmailButtonId="send-email-summary-button"
                  emailButtonId="email-summary-button"
                  exportButtonId="download-summary-as-pdf-button"
                />
                <LoadingOverlay
                  isLoading={isModalLoading}
                  generationType="summary"
                  showSocketMessage={isModalLoading}
                />
              </Box>

              <DynamicFooterWithInput
                description={
                  <>
                    I&apos;ve created this <span>candidate summary</span>! This summary compares
                    your application against the job requirements.
                  </>
                }
                isLoading={isGenerating}
                submitCallback={handleRefineSubmit}
                suggestedPrompts={SUGGESTED_PROMPTS}
              />
            </Stack>
          ) : (
            <Stack sx={{ alignItems: 'center', rowGap: 2 }}>
              <Stack sx={{ width: '40px' }}>
                <FelixConfused />
              </Stack>
              <Stack sx={{ color: '#939393', fontWeight: 600 }}>No summary available</Stack>
              <Stack
                id="generate-summary-button"
                sx={{
                  ...styles.generateSummaryButton,
                  minWidth: '155px',
                  justifyContent: 'center'
                }}
                onClick={() => generateSummary(undefined)}
              >
                {updatingSummary ? (
                  <CircularProgress size={18} color="inherit" sx={{ marginRight: '4px' }} />
                ) : (
                  'Generate summary'
                )}
              </Stack>
            </Stack>
          )}
        </>
      ) : (
        <AISummaryNoAccess />
      )}
    </Stack>
  );
}
